import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaUsers,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaShoppingCart,
  FaMobileAlt,
  FaFilePdf,
  FaSpinner,
  FaGift,
  FaStore,
  FaUndo,
  FaBoxOpen,
} from 'react-icons/fa';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  getWalkInCustomerAnalytics,
  downloadWalkInCustomerAnalyticsPDF,
  getBranchNamesForUser,
  pipGetConfig,
  pipUpdateConfig,
} from '../../services/ClientService';

const formatINR = (n) => {
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(n || 0));
  } catch {
    return `₹${Number(n || 0).toFixed(0)}`;
  }
};

const ALL_BRANCHES = 'all';
const PERIODS = ['Today', 'Weekly', 'Monthly', '1 Month'];
const TYPE_FILTERS = [
  { value: 'all', label: 'All POS orders' },
  { value: 'behaviour', label: 'Behaviour Payment' },
  { value: 'classic', label: 'Classic POS' },
];
const METHOD_FILTERS = [
  { value: 'all', label: 'All methods' },
  { value: 'Cash', label: 'Cash' },
  { value: 'Card', label: 'Card' },
  { value: 'UPI', label: 'UPI' },
];
const PIE_COLORS = ['#4f46e5', '#0d9488', '#f59e0b', '#ef4444', '#64748b'];

const PosCustomerAnalytics = () => {
  const userData = useMemo(() => JSON.parse(localStorage.getItem('userData') || '{}'), []);
  const [timePeriod, setTimePeriod] = useState('1 Month');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [selectedBranchId, setSelectedBranchId] = useState(ALL_BRANCHES);
  const [branches, setBranches] = useState([]);
  const [canSwitchBranch, setCanSwitchBranch] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    customers: 0,
    orders: 0,
    revenue: 0,
    refundedAmount: 0,
    repeatCustomers: 0,
    behaviourOrders: 0,
    classicOrders: 0,
    behaviourRevenue: 0,
    classicRevenue: 0,
    offerSpend: 0,
    offerOrders: 0,
    returnsRefund: 0,
    returnsQty: 0,
    returnsRestockedQty: 0,
    returnsDamagedQty: 0,
    returnsLines: 0,
  });
  const [charts, setCharts] = useState({ daily: [], paymentMethods: [], behaviourSplit: [] });
  const [returnsItems, setReturnsItems] = useState([]);
  const [returnsSummary, setReturnsSummary] = useState({
    totalRefund: 0,
    totalQty: 0,
    damagedQty: 0,
    justReturnQty: 0,
    restockedQty: 0,
    totalLines: 0,
  });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [snack, setSnack] = useState(null);
  const [behaviourRequireMobile, setBehaviourRequireMobile] = useState(true);
  const [behaviourRequireCustomerName, setBehaviourRequireCustomerName] = useState(true);
  const [behaviourForcedOn, setBehaviourForcedOn] = useState(true);
  const [walkInReturnBillLookback, setWalkInReturnBillLookback] = useState('today');
  const [loadingBehaviourRules, setLoadingBehaviourRules] = useState(true);
  const [savingBehaviourRules, setSavingBehaviourRules] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (!snack) return undefined;
    const t = setTimeout(() => setSnack(null), 2800);
    return () => clearTimeout(t);
  }, [snack]);

  useEffect(() => {
    (async () => {
      setLoadingBehaviourRules(true);
      try {
        const res = await pipGetConfig({});
        if (res?.success && res.data) {
          setBehaviourForcedOn(res.data.behaviourForcedOn !== false);
          setBehaviourRequireMobile(res.data.behaviourRequireMobile !== false);
          setBehaviourRequireCustomerName(res.data.behaviourRequireCustomerName !== false);
          setWalkInReturnBillLookback(
            ['today', 'past_7_days', 'all'].includes(res.data.walkInReturnBillLookback)
              ? res.data.walkInReturnBillLookback
              : 'today'
          );
        }
      } catch {
        /* keep defaults: Behaviour forced ON, both fields mandatory */
      } finally {
        setLoadingBehaviourRules(false);
      }
    })();
  }, []);

  const persistBehaviourFieldRule = async (patch) => {
    if (savingBehaviourRules) return;
    const prevForced = behaviourForcedOn;
    const prevMobile = behaviourRequireMobile;
    const prevName = behaviourRequireCustomerName;
    const prevLookback = walkInReturnBillLookback;
    if (patch.behaviourForcedOn !== undefined) {
      setBehaviourForcedOn(!!patch.behaviourForcedOn);
    }
    if (patch.behaviourRequireMobile !== undefined) {
      setBehaviourRequireMobile(!!patch.behaviourRequireMobile);
    }
    if (patch.behaviourRequireCustomerName !== undefined) {
      setBehaviourRequireCustomerName(!!patch.behaviourRequireCustomerName);
    }
    if (patch.walkInReturnBillLookback !== undefined) {
      setWalkInReturnBillLookback(patch.walkInReturnBillLookback);
    }
    setSavingBehaviourRules(true);
    try {
      const res = await pipUpdateConfig(patch);
      if (res?.success && res.data) {
        setBehaviourForcedOn(res.data.behaviourForcedOn !== false);
        setBehaviourRequireMobile(res.data.behaviourRequireMobile !== false);
        setBehaviourRequireCustomerName(res.data.behaviourRequireCustomerName !== false);
        setWalkInReturnBillLookback(
          ['today', 'past_7_days', 'all'].includes(res.data.walkInReturnBillLookback)
            ? res.data.walkInReturnBillLookback
            : 'today'
        );
        setSnack({
          type: 'ok',
          text: patch.behaviourForcedOn !== undefined
            ? (res.data.behaviourForcedOn !== false
              ? 'Behaviour ON — Merchant Billing locked to Behaviour'
              : 'Behaviour OFF — cashiers can toggle Behaviour on Merchant Billing')
            : 'Settings updated',
        });
      } else {
        setBehaviourForcedOn(prevForced);
        setBehaviourRequireMobile(prevMobile);
        setBehaviourRequireCustomerName(prevName);
        setWalkInReturnBillLookback(prevLookback);
        setSnack({ type: 'err', text: res?.message || 'Could not update settings' });
      }
    } catch {
      setBehaviourForcedOn(prevForced);
      setBehaviourRequireMobile(prevMobile);
      setBehaviourRequireCustomerName(prevName);
      setWalkInReturnBillLookback(prevLookback);
      setSnack({ type: 'err', text: 'Could not update settings' });
    } finally {
      setSavingBehaviourRules(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const brRes = await getBranchNamesForUser();
        if (brRes?.success && Array.isArray(brRes.data)) {
          const list = brRes.data.filter((b) => !b.isWarehouse);
          setBranches(list);
          const canSwitch = brRes.canSwitchBranch !== false && list.length > 1;
          setCanSwitchBranch(canSwitch);
          if (!canSwitch && list.length === 1) {
            setSelectedBranchId(String(list[0]._id));
          } else {
            const preferred = userData.selectedBranch || userData._id || null;
            const match = preferred && list.find((b) => String(b._id) === String(preferred));
            setSelectedBranchId(match ? String(match._id) : ALL_BRANCHES);
          }
        }
      } catch {
        /* keep defaults */
      }
    })();
  }, [userData._id, userData.selectedBranch]);

  const load = useCallback(async () => {
    if (!selectedBranchId) return;
    setLoading(true);
    try {
      const res = await getWalkInCustomerAnalytics({
        timePeriod,
        searchTerm: debouncedSearch || undefined,
        paymentTypeFilter,
        paymentMethodFilter,
        selectedBranchId,
        skip: 0,
        limit: 150,
      });
      if (res?.success) {
        setRows(res.data || []);
        setSummary(res.summary || {});
        setCharts(res.charts || { daily: [], paymentMethods: [], behaviourSplit: [] });
        setReturnsItems(res.returns?.items || []);
        setReturnsSummary(res.returns?.summary || {
          totalRefund: 0,
          totalQty: 0,
          damagedQty: 0,
          justReturnQty: 0,
          restockedQty: 0,
          totalLines: 0,
        });
      } else {
        setRows([]);
        setCharts({ daily: [], paymentMethods: [], behaviourSplit: [] });
        setReturnsItems([]);
        setSummary((s) => ({ ...s, offerSpend: 0, offerOrders: 0, refundedAmount: 0, returnsRefund: 0 }));
      }
    } catch {
      setRows([]);
      setCharts({ daily: [], paymentMethods: [], behaviourSplit: [] });
      setReturnsItems([]);
    } finally {
      setLoading(false);
    }
  }, [timePeriod, debouncedSearch, paymentTypeFilter, paymentMethodFilter, selectedBranchId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      await downloadWalkInCustomerAnalyticsPDF({
        timePeriod,
        searchTerm: debouncedSearch || undefined,
        paymentTypeFilter,
        paymentMethodFilter,
        selectedBranchId,
      });
      setSnack({ type: 'ok', text: 'PDF downloaded' });
    } catch {
      setSnack({ type: 'err', text: 'PDF download failed' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/40 p-4 md:p-8">
      <div className="space-y-6">
        {snack && (
          <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow text-sm text-white ${snack.type === 'ok' ? 'bg-emerald-600' : 'bg-red-600'}`}>
            {snack.text}
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">Template Manager · POS</p>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
              <FaUsers className="text-indigo-600" /> Customer Analytics
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Behaviour + Classic POS walk-ins. Revenue is <span className="font-semibold text-slate-700">net after returns</span>.
              Historic months merge from salesBackup.
            </p>
          </div>
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={downloading || loading}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
          >
            {downloading ? <FaSpinner className="animate-spin" /> : <FaFilePdf />}
            Download PDF
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="text-xs text-slate-500">Mobile customers</div>
            <div className="text-2xl font-bold text-slate-900">{summary.customers || 0}</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="text-xs text-slate-500">POS orders</div>
            <div className="text-2xl font-bold text-slate-900">{summary.orders || 0}</div>
            <div className="text-[11px] text-slate-400 mt-1">
              B {summary.behaviourOrders || 0} · C {summary.classicOrders || 0}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="text-xs text-slate-500">Net revenue</div>
            <div className="text-2xl font-bold text-slate-900">{formatINR(summary.revenue)}</div>
            {(summary.refundedAmount > 0) && (
              <div className="text-[11px] text-rose-600 mt-1">
                Refunded {formatINR(summary.refundedAmount)}
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl border border-rose-200 p-4 shadow-sm bg-gradient-to-br from-rose-50 to-white">
            <div className="text-xs text-rose-700 flex items-center gap-1">
              <FaUndo /> Returns refund
            </div>
            <div className="text-2xl font-bold text-rose-800">{formatINR(summary.returnsRefund || returnsSummary.totalRefund)}</div>
            <div className="text-[11px] text-rose-600/80 mt-1">
              {summary.returnsQty || returnsSummary.totalQty || 0} units · {summary.returnsLines || returnsSummary.totalLines || 0} lines
            </div>
          </div>
          <div className="bg-white rounded-xl border border-emerald-200 p-4 shadow-sm bg-gradient-to-br from-emerald-50 to-white">
            <div className="text-xs text-emerald-700 flex items-center gap-1">
              <FaBoxOpen /> Stock restocked
            </div>
            <div className="text-2xl font-bold text-emerald-800">
              {summary.returnsRestockedQty || returnsSummary.restockedQty || 0}
            </div>
            <div className="text-[11px] text-amber-700 mt-1">
              Damaged (not restocked): {summary.returnsDamagedQty || returnsSummary.damagedQty || 0}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-amber-200 p-4 shadow-sm bg-gradient-to-br from-amber-50 to-white">
            <div className="text-xs text-amber-700 flex items-center gap-1">
              <FaGift /> Offer spend
            </div>
            <div className="text-2xl font-bold text-amber-800">{formatINR(summary.offerSpend)}</div>
            <div className="text-[11px] text-amber-600/80 mt-1">
              {summary.offerOrders || 0} bill{(summary.offerOrders || 0) === 1 ? '' : 's'} · Repeat {summary.repeatCustomers || 0}
            </div>
          </div>
        </div>


        <div className="bg-white rounded-xl border border-rose-100 px-4 py-3 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">Walk-in Returns · Bill list window</p>
            <p className="text-[11px] text-slate-500 truncate">
              Bills cashiers can browse on Merchant Billing → Returns
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {(loadingBehaviourRules || savingBehaviourRules) && (
              <FaSpinner className="animate-spin text-rose-500 text-sm" />
            )}
            <select
              value={walkInReturnBillLookback}
              disabled={loadingBehaviourRules || savingBehaviourRules}
              onChange={(e) => persistBehaviourFieldRule({ walkInReturnBillLookback: e.target.value })}
              className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-800 disabled:opacity-50 min-w-[140px]"
            >
              <option value="today">Today</option>
              <option value="past_7_days">Past 7 days</option>
              <option value="all">All bills</option>
            </select>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-indigo-100 p-4 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <FaMobileAlt className="text-indigo-600" />
                Behaviour Payment · Field rules
              </h2>
              <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                When <span className="font-semibold text-slate-700">Behaviour</span> is ON, Merchant Billing stays locked
                to Behaviour mode (cashiers cannot turn it off). Field rules below apply only while Behaviour is ON.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {(loadingBehaviourRules || savingBehaviourRules) && (
                <FaSpinner className="animate-spin text-indigo-500" />
              )}
              <div className="inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50/70 px-3 py-1.5">
                <span className={`text-xs font-semibold ${behaviourForcedOn ? 'text-indigo-700' : 'text-slate-500'}`}>
                  Behaviour
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={behaviourForcedOn}
                  disabled={loadingBehaviourRules || savingBehaviourRules}
                  onClick={() => persistBehaviourFieldRule({ behaviourForcedOn: !behaviourForcedOn })}
                  title={
                    behaviourForcedOn
                      ? 'ON — Merchant Billing Behaviour is locked ON'
                      : 'OFF — cashiers can toggle Behaviour on Merchant Billing'
                  }
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                    behaviourForcedOn ? 'bg-indigo-600' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      behaviourForcedOn ? 'translate-x-[22px]' : 'translate-x-[4px]'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${behaviourForcedOn ? '' : 'opacity-60'}`}>
            <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">Make Mobile Number mandatory</p>
                <p className="text-[11px] text-slate-500">Require a valid 10-digit mobile when Behaviour is ON</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={behaviourRequireMobile}
                disabled={loadingBehaviourRules || savingBehaviourRules}
                onClick={() => persistBehaviourFieldRule({ behaviourRequireMobile: !behaviourRequireMobile })}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                  behaviourRequireMobile ? 'bg-indigo-600' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    behaviourRequireMobile ? 'translate-x-[22px]' : 'translate-x-[4px]'
                  }`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">Make Customer Name mandatory</p>
                <p className="text-[11px] text-slate-500">Require customer name when Behaviour is ON</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={behaviourRequireCustomerName}
                disabled={loadingBehaviourRules || savingBehaviourRules}
                onClick={() => persistBehaviourFieldRule({ behaviourRequireCustomerName: !behaviourRequireCustomerName })}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                  behaviourRequireCustomerName ? 'bg-indigo-600' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    behaviourRequireCustomerName ? 'translate-x-[22px]' : 'translate-x-[4px]'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm space-y-3">
          <div className="flex flex-col lg:flex-row gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search mobile, name, or order id…"
                className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white"
              />
            </div>
            <div className="relative min-w-[180px]">
              <FaStore className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <select
                value={selectedBranchId}
                onChange={(e) => setSelectedBranchId(e.target.value)}
                disabled={!canSwitchBranch && branches.length <= 1}
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold disabled:opacity-60"
              >
                {canSwitchBranch && (
                  <option value={ALL_BRANCHES}>All branches</option>
                )}
                {branches.map((b) => (
                  <option key={String(b._id)} value={String(b._id)}>
                    {b.branchName || b.name || 'Branch'}
                    {b.masterAccount ? ' (HQ)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <select
              value={timePeriod}
              onChange={(e) => setTimePeriod(e.target.value)}
              className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold"
            >
              {PERIODS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <select
              value={paymentTypeFilter}
              onChange={(e) => setPaymentTypeFilter(e.target.value)}
              className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold"
            >
              {TYPE_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
            <select
              value={paymentMethodFilter}
              onChange={(e) => setPaymentMethodFilter(e.target.value)}
              className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold"
            >
              {METHOD_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Daily orders & revenue</h3>
            <div className="h-56">
              {(charts.daily || []).length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-slate-400">No chart data</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={charts.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v, name) => (name === 'revenue' ? formatINR(v) : v)} />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="orders" stroke="#4f46e5" strokeWidth={2} dot={false} name="Orders" />
                    <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#0d9488" strokeWidth={2} dot={false} name="Revenue" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Behaviour vs Classic</h3>
            <div className="h-56">
              {(charts.behaviourSplit || []).every((x) => !x.orders) ? (
                <div className="h-full flex items-center justify-center text-sm text-slate-400">No chart data</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={charts.behaviourSplit}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v, name) => (name === 'revenue' ? formatINR(v) : v)} />
                    <Legend />
                    <Bar dataKey="orders" fill="#4f46e5" name="Orders" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="revenue" fill="#0d9488" name="Revenue" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm lg:col-span-2">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Payment methods</h3>
            <div className="h-56 grid grid-cols-1 md:grid-cols-2 gap-2">
              {(charts.paymentMethods || []).length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-slate-400 md:col-span-2">No chart data</div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={charts.paymentMethods}
                        dataKey="orders"
                        nameKey="method"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ method, orders }) => `${method} (${orders})`}
                      >
                        {(charts.paymentMethods || []).map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={charts.paymentMethods}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="method" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => formatINR(v)} />
                      <Bar dataKey="revenue" fill="#6366f1" name="Revenue" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-slate-500 text-sm">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-slate-500 text-sm px-6">
              No POS orders for these filters. Classic Cash/Card/UPI and Behaviour Payment orders both appear here.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {rows.map((row) => {
                const key = row.customerKey || row.mobile || 'classic';
                const open = !!expanded[key];
                return (
                  <li key={key} className="p-4 hover:bg-slate-50/80">
                    <button
                      type="button"
                      className="w-full text-left flex items-start justify-between gap-3"
                      onClick={() => setExpanded((p) => ({ ...p, [key]: !p[key] }))}
                    >
                      <div>
                        <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                          {row.isBehaviour ? (
                            <>
                              <FaMobileAlt className="text-indigo-500 text-sm" />
                              {row.mobile}
                            </>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-teal-50 text-teal-800 text-xs font-bold">
                              Classic · {row.recentOrders?.[0]?.method || 'POS'}
                            </span>
                          )}
                          <span className="text-slate-500 font-normal">
                            {row.isBehaviour
                              ? (row.customerName || '—')
                              : (row.recentOrders?.[0]?.walkInOrderId || row.customerName || '—')}
                          </span>
                          <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${row.isBehaviour ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-600'}`}>
                            {row.isBehaviour ? 'Behaviour' : 'Classic'}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3">
                          <span>{row.orderCount} orders</span>
                          <span>{formatINR(row.totalSpend)} spend</span>
                          <span>Avg {formatINR(row.avgBill)}</span>
                          <span>UPI {row.upiOrders || 0} · Card {row.cardOrders || 0} · Cash {row.cashOrders || 0}</span>
                        </div>
                      </div>
                      {open ? <FaChevronUp className="text-slate-400 mt-1" /> : <FaChevronDown className="text-slate-400 mt-1" />}
                    </button>
                    {open && (
                      <div className="mt-3 ml-1 pl-3 border-l-2 border-indigo-200 space-y-2">
                        <p className="text-xs font-semibold text-slate-600 flex items-center gap-1">
                          <FaShoppingCart /> Recent POS orders
                        </p>
                        {(row.recentOrders || []).map((o, idx) => (
                          <div key={`${o.walkInOrderId}-${idx}`} className="text-xs bg-slate-50 rounded-lg px-3 py-2 flex flex-wrap justify-between gap-2">
                            <span className="font-mono text-slate-700">{o.walkInOrderId}</span>
                            <span className="text-slate-500">{o.method}</span>
                            <span className={`font-semibold ${o.paymentType === 'behaviour' ? 'text-indigo-600' : 'text-slate-600'}`}>
                              {o.paymentType === 'behaviour' ? 'Behaviour' : 'Classic'}
                            </span>
                            <span className="font-semibold">{formatINR(o.amount)}</span>
                            {o.refundedAmount > 0 && (
                              <span className="text-rose-600 font-semibold">
                                Refund -{formatINR(o.refundedAmount)}
                              </span>
                            )}
                            {o.offerDiscount > 0 && (
                              <span className="text-amber-700 font-semibold">
                                Offer -{formatINR(o.offerDiscount)}
                                {o.offerName ? ` · ${o.offerName}` : ''}
                              </span>
                            )}
                            <span className="text-slate-400">{o.orderPlacedOn ? new Date(o.orderPlacedOn).toLocaleString() : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-rose-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-rose-100 bg-gradient-to-r from-rose-50 to-white flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <FaUndo className="text-rose-600" /> Returned items
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Same period as filters above · Just return restocks sellable stock · Damaged does not
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-[11px] font-semibold">
              <span className="px-2.5 py-1 rounded-lg bg-rose-100 text-rose-800">
                Refund {formatINR(returnsSummary.totalRefund || summary.returnsRefund)}
              </span>
              <span className="px-2.5 py-1 rounded-lg bg-emerald-100 text-emerald-800">
                Restocked {returnsSummary.restockedQty || summary.returnsRestockedQty || 0}
              </span>
              <span className="px-2.5 py-1 rounded-lg bg-amber-100 text-amber-800">
                Damaged {returnsSummary.damagedQty || summary.returnsDamagedQty || 0}
              </span>
            </div>
          </div>
          {loading ? (
            <div className="py-12 text-center text-slate-500 text-sm">Loading returns…</div>
          ) : returnsItems.length === 0 ? (
            <div className="py-12 text-center text-slate-500 text-sm px-6">
              No returned items in this period.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Invoice</th>
                    <th className="px-4 py-3 text-left font-medium">Product</th>
                    <th className="px-4 py-3 text-left font-medium">Option</th>
                    <th className="px-4 py-3 text-right font-medium">Qty</th>
                    <th className="px-4 py-3 text-right font-medium">Unit ₹</th>
                    <th className="px-4 py-3 text-right font-medium">Refund</th>
                    <th className="px-4 py-3 text-left font-medium">Reason</th>
                    <th className="px-4 py-3 text-left font-medium">Stock</th>
                    <th className="px-4 py-3 text-left font-medium">When</th>
                    <th className="px-4 py-3 text-left font-medium">Customer</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {returnsItems.map((r) => (
                    <tr key={String(r._id)} className="hover:bg-rose-50/40">
                      <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">
                        {r.walkInOrderId || '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-800">
                        {r.productName}
                        {r.isFreeOfferItem && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">
                            Free gift
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{r.optionLabel || '—'}</td>
                      <td className="px-4 py-3 text-right font-medium">{r.quantity}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{formatINR(r.unitPrice)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-rose-700">{formatINR(r.refundAmount)}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                            r.reason === 'damaged'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-emerald-100 text-emerald-800'
                          }`}
                        >
                          {r.reasonLabel || (r.reason === 'damaged' ? 'Damaged' : 'Just return')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs font-medium text-slate-600">
                        {r.stockImpact || (r.reason === 'damaged' ? 'Not restocked' : 'Restocked')}
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                        {r.returnedAt
                          ? new Date(r.returnedAt).toLocaleString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {r.customerPhone || r.customerName || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(returnsSummary.totalLines || 0) > returnsItems.length && (
                <p className="px-4 py-3 text-xs text-slate-500 border-t border-slate-100">
                  Showing latest {returnsItems.length} of {returnsSummary.totalLines} return lines for this period.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PosCustomerAnalytics;
